import type { AisTrackForm, ShipForm, ShipHistoryForm, ShipQueryParam, ShipVo } from "../../../../common/types/ship";
import type { Page } from "../../../../common/types/base/BaseEntity";
import { BaseService } from "./baseService";
import httpRequest from "./index";
import type { Ship, ShipDto, ShipInfoVo, ShipSmartVo } from "@zhihaoscm/entity";
import { debounce } from "lodash-es";
import type { GeoPoint } from "@zhihaoscm/entity";

export interface VehicleQueryLimitInfo {
    /**
     * 会员等级
     */
    memberLevel: number;
    /**
     * 会员等级名称
     */
    memberLevelName?: string;
    /**
     * 是否超过次数限制 1超过 0未超过
     */
    overFlag: number;
    /**
     * 是否不限次 1不限次 0限制次数
     */
    hasUnLimit: number;
    /**
     * 每日总次数
     */
    totalTimes: number;
    /**
     * 已使用次数
     */
    usedTimes: number;
    /**
     * 剩余次数
     */
    remainingTimes: number;
    /**
     * 是否可升级 1可升级 0不可升级
     */
    canUpgrade: number;
    /**
     * 是否最高等级 1是 0否
     */
    highestLevel: number;
}

export class ShipService extends BaseService<Ship, ShipVo, ShipQueryParam, ShipForm, Page<Ship>> {
    /**
     * 分页查询
     * @param params
     */
    async paging(params: ShipQueryParam): Promise<Page<ShipVo>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    /**
     * 按 ID 获取对象
     * @param id
     */
    async getInfo(id: number): Promise<ShipVo> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }

    // 更新
    async update(id: string | number, form: ShipForm): Promise<Ship> {
        const { data } = await this.service.put(`${this.path}/${id}`, form);
        return data;
    }

    // 删除船舶
    async deleteShip(id: number): Promise<ShipVo> {
        const { data } = await this.service.get(`${this.path}/update/state`, { params: { id } });
        return data;
    }

    public async search(keyword: string, limit: number = 10, type: string = "SHIP"): Promise<Ship[]> {
        const { data } = await this.service.get(`${this.path}/keyword`, { params: { keyword, limit, type } });
        return data;
    }

    async findCreate(id: string): Promise<ShipInfoVo> {
        return debounce(
            async () => {
                const { data } = await this.service.get(`${this.path}/find-create/${id}`);
                return data;
            },
            500,
            { leading: true, trailing: false }
        )();
    }

    /**
     * 下拉选框列表数据
     */
    public async selector(params: any): Promise<Page<Ship>> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }

    public async getHistoryList(): Promise<Ship[]> {
        const { data } = await this.service.get(`${this.path}/history`);
        return data;
    }

    public async clearHistory(): Promise<void> {
        const { data } = await this.service.delete(`${this.path}/all-history`);
        return data;
    }

    public async deleteHistory(form: ShipHistoryForm): Promise<void> {
        const { data } = await this.service.delete(`${this.path}/history`, { data: form });
        return data;
    }

    public async addHistory(form: ShipHistoryForm): Promise<void> {
        const { data } = await this.service.post(`${this.path}/history`, form);
        return data;
    }

    public async extent(form: ShipDto): Promise<ShipInfoVo[]> {
        const { data } = await this.service.post(
            `${this.path}/extent`,
            {
                ...form
            },
            {
                timeout: 60000
            }
        );
        return data;
    }

    public async aisTrack(form: AisTrackForm): Promise<any[]> {
        const { data } = await this.service.get(`${this.path}/aisTrack`, {
            params: {
                mmsi: form.mmsi,
                start: new Date(form.start).getTime(),
                end: new Date(form.end).getTime()
            }
        });
        return data;
    }
    /**
     * 根据货主找船需求智能推荐船舶
     */
    public async getShipRecommend(id: number): Promise<ShipSmartVo[]> {
        const { data } = await this.service.get(`${this.path}/find/custom/${id}`);
        return data;
    }
    /**
     * 查询已报价船舶
     */
    public async getShipAccept(id: number): Promise<ShipSmartVo[]> {
        const { data } = await this.service.get(`${this.path}/find/accept/${id}`);
        return data;
    }

    /**
     * 自定义艘船
     */
    public async getShipCustom(params: { geoPointList: GeoPoint[]; time?: number }): Promise<ShipInfoVo[]> {
        const { data } = await this.service.post(`${this.path}/extent/custom`, params, {
            timeout: 60000
        });
        return data;
    }

    /**
     * 查询查船、查车次数是否超限  	1查船 2查车
     *
     */
    public async findLimitNum(params: { type: number }): Promise<VehicleQueryLimitInfo> {
        const { data } = await this.service.get(`${this.path}/findLimitNum`, { params });
        return data;
    }

    /**
     * 查船-航线轨迹规划
     *
     */
    public async transportPlanning(params: {sourceId: number,destinationId:number}) {
        const { data } = await this.service.get(`${this.path}/transport-planning`, { params });
        return data;
    }

    /**
     * 查船-航线航程计算
     * 
     */
    public async transportCompute(params: {
        sourceGeoPoint: {
            lng?: number,
            lat?: number
        },
        destinationGeoPoint: {
            lng?: number,
            lat?: number
        }
    }) {
        const { data } = await this.service.post(`${this.path}/transport-compute`,  params );
        return data;
    }
}

export const shipService = new ShipService("/ship", httpRequest);
